import { isEmpty, orderBy, values } from "lodash";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import FeatureExplanation from "../../../components/FeatureExplanation";
import LoadingIcon from "../../../components/icons/LoadingIcon";
import { useAuthentication } from "../../../context/authentication";
import { useCommissionReports } from "../../../context/commissionReports";
import { getCommissionReportTitle } from "../../../context/commissionReports/CommissionReportsProvider";
import { DottedMenu } from "../../../context/dropdownMenu/DropdownMenuProvider";
import {
  LoadingKey,
  useLoading
} from "../../../context/loading/LoadingProvider";
import { useMembers } from "../../../context/members/MembersProvider";
import { useWorkspaces } from "../../../context/workspaces";
import { Entity } from "../../../utils/constants";
import { formatDateToLocale } from "../../../utils/dates";
import timezones from "../../../utils/timezones";
import { CommissionReport } from "../../../utils/types";
import useCurrency, { CurrencyKey } from "../../../utils/useCurrency";
import { useHntMarket } from "../../../utils/useHntMarket";
import EntityDataTable from "../common/EntityDataTable";
import TablePropertiesSidebar from "../common/TablePropertiesSidebar";
import useDataTable, { Property } from "../common/useDataTable";
import { PaginationKey } from "../common/usePagination";
import LockedWorkspace from "../LockedWorkspace";
import LockedWorkspaceByCommunityPlan from "../LockedWorkspaceByCommunityPlan";
import LockedWorkspaceByMembers from "../LockedWorkspaceByMembers";
import CommissionReportActionsButton from "./CommissionReportActionsButton";

interface Props extends RouteComponentProps<{}> {}

const CommissionReports: React.FC<Props> = (props) => {
  const { getIsReady } = useLoading();
  const { wrapActionWithAuth } = useAuthentication();
  const [showPropertiesSidebar, setShowPropertiesSidebar] = useState(false);
  const sortOrder = useDataTable(
    (state) => state.sortOrder[Entity.commissionReport]
  );
  const { getCurrencyTitle, currencyDisplay } = useCurrency();
  const { displayInCurrency } = useHntMarket();
  const { commissionReports, setGenerateCommissionReportSidebarConfig } =
    useCommissionReports();
  const { currentWorkspaceIsLocked, currentWorkspaceAllowedForCommunityPlan } =
    useWorkspaces();
  const { membersLimitExceeded } = useMembers();

  const properties: { [key: string]: Property<CommissionReport> } = useMemo(
    () => ({
      open: {
        key: "open",
        title: "Status",
        style: { minWidth: "9rem" },
        renderContent: (commissionReport) => {
          switch (commissionReport.status) {
            case "pending":
              return (
                <div className="flex items-center">
                  <LoadingIcon className="animate-spin mr-2 h-4 w-4" />
                  <span className="text-sm text-gray-500 font-medium dark:text-gray-50">
                    Processing
                  </span>
                </div>
              );
            case "completed":
              return (
                <Link
                  to={`commission-reports/${commissionReport.uuid}`}
                  className="inline-flex items-center px-2.5 py-1.5 border shadow-sm text-xs text-white font-medium rounded bg-hotspotty-400 border-hotspotty-500 hover:bg-hotspotty-500"
                >
                  Open #{commissionReport.identifier}
                </Link>
              );
            case "failed":
              return (
                <span className="text-sm font-medium text-red-400">Failed</span>
              );
            default:
              return null;
          }
        }
      },
      title: {
        key: "title",
        title: "Title",
        style: { minWidth: "16rem" },
        sortIteratee: "title",
        renderContent: (commissionReport) => (
          <Link
            className="hover:underline"
            to={`/workspace/commission-reports/${commissionReport.uuid}`}
          >
            {getCommissionReportTitle(commissionReport)}
          </Link>
        )
      },
      created_at: {
        key: "created_at",
        title: "Created at",
        style: { minWidth: "13rem" },
        sortIteratee: "created_at",
        renderContent: ({ created_at }) => (
          <span className="text-sm text-gray-500 font-medium dark:text-gray-50">
            {formatDateToLocale(created_at, true)}
          </span>
        )
      },
      period: {
        key: "period",
        title: "Period",
        style: { minWidth: "8rem" },
        renderContent: getCommissionReportPeriodText
      },
      start_date: {
        key: "start_date",
        title: "Start date",
        style: { minWidth: "10rem" },
        renderContent: ({ start_date }) => formatDateToLocale(start_date)
      },
      end_date: {
        key: "end_date",
        title: "End date",
        style: { minWidth: "10rem" },
        renderContent: ({ end_date }) => formatDateToLocale(end_date)
      },
      timezone: {
        key: "timezone",
        title: "Timezone",
        style: { minWidth: "17rem" },
        sortIteratee: "timezone",
        renderContent: ({ timezone }) => timezones[timezone]
      },
      currency: {
        key: "currency",
        title: "Currency",
        sortIteratee: "currency",
        renderContent: ({ currency }) => getCurrencyTitle(currency)
      },
      total_commission_amount: {
        key: "total_commission_amount",
        title: "Commissions",
        style: { minWidth: "13rem" },
        sortIteratee: "total_commission_amount",
        renderContent: ({ currency, total_commission_amount }) =>
          currency === CurrencyKey.hnt
            ? displayInCurrency(total_commission_amount, {
                withHntSuffix: true,
                needsDivision: true
              })
            : currencyDisplay(currency, total_commission_amount)
      },
      actions: {
        key: "actions",
        title: "Actions",
        renderContent: (commissionReport) => (
          <CommissionReportActionsButton
            disabled={commissionReport.status === "pending"}
            commissionReport={commissionReport}
          />
        )
      }
    }),
    [displayInCurrency, getCurrencyTitle, currencyDisplay]
  );

  const isLoading = useMemo(
    () =>
      isEmpty(commissionReports) &&
      !getIsReady([LoadingKey.workspaceCommissionReports]),
    [commissionReports, getIsReady]
  );

  const sortedCommissionReports = useMemo(() => {
    let sortedCommissionReports = values(commissionReports);

    if (sortOrder) {
      sortedCommissionReports = orderBy(
        sortedCommissionReports,
        [sortOrder.iteratee],
        [sortOrder.order]
      );
    }

    return sortedCommissionReports;
  }, [commissionReports, sortOrder]);

  const renderTable = useMemo(() => {
    if (currentWorkspaceIsLocked) {
      return (
        <LockedWorkspace
          title="Payment failed"
          paragraphs={[
            "Your subscription payment failed. Please resolve the issue with your payment method."
          ]}
          buttonText="Update payment details"
          buttonLink="/workspaces/settings/billing"
        />
      );
    }

    if (!currentWorkspaceAllowedForCommunityPlan) {
      return <LockedWorkspaceByCommunityPlan />;
    }

    if (membersLimitExceeded) {
      return <LockedWorkspaceByMembers />;
    }

    if (isLoading) return <CenteredLoading className="h-96" />;

    return (
      <>
        <div className="p-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
            <span className="flex-1">Commission reports</span>

            <DottedMenu
              text="Open options"
              menuItems={[
                {
                  text: "Edit columns",
                  onClick: () => {
                    setShowPropertiesSidebar(true);
                  }
                }
              ]}
            />

            <button
              type="button"
              className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
              onClick={wrapActionWithAuth(() => {
                setGenerateCommissionReportSidebarConfig({
                  show: true,
                  contacts: []
                });
              })}
            >
              Generate report
            </button>
          </h3>
        </div>

        <EntityDataTable<CommissionReport>
          paginationKey={PaginationKey.workspaceCommissionReports}
          entity={Entity.commissionReport}
          items={sortedCommissionReports}
          properties={properties}
        />
      </>
    );
  }, [
    currentWorkspaceAllowedForCommunityPlan,
    currentWorkspaceIsLocked,
    isLoading,
    membersLimitExceeded,
    properties,
    setGenerateCommissionReportSidebarConfig,
    sortedCommissionReports,
    wrapActionWithAuth
  ]);

  return (
    <div className="overflow-y-auto">
      <TablePropertiesSidebar<CommissionReport>
        entity={Entity.commissionReport}
        properties={properties}
        open={showPropertiesSidebar}
        setOpen={setShowPropertiesSidebar}
      />

      {!currentWorkspaceIsLocked &&
        currentWorkspaceAllowedForCommunityPlan &&
        !membersLimitExceeded && (
          <FeatureExplanation
            className="sm:my-4 sm:mx-4"
            paragraphs={[
              <>
                Calculate and track payouts for{" "}
                <Link
                  className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
                  to="/workspace/contacts"
                >
                  contacts
                </Link>{" "}
                that facilitate your{" "}
                <Link
                  className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
                  to="/workspace/hotspots"
                >
                  hotspot
                </Link>{" "}
                deployments. Add{" "}
                <Link
                  className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
                  to="/workspace/installs"
                >
                  installs
                </Link>{" "}
                and link commissions for your{" "}
                <Link
                  className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
                  to="/workspace/contacts"
                >
                  contacts
                </Link>{" "}
                to get monthly commission reports.
              </>
            ]}
          />
        )}

      {renderTable}
    </div>
  );
};

export default CommissionReports;

//
// Utils
//

export const getCommissionReportPeriodText = ({
  start_date,
  end_date
}: CommissionReport) => {
  const conditionalYearFormat =
    moment(start_date).utc().year() === moment().utc().year() ? "" : " YYYY";
  const isSameMonth =
    moment(start_date).utc().month() === moment(end_date).utc().month();

  if (
    moment(start_date).utc().date() ===
      moment(start_date).utc().startOf("month").date() &&
    moment(end_date).utc().date() ===
      moment(end_date).utc().endOf("month").date()
  ) {
    // In this case the period covers the full month(s)
    return isSameMonth
      ? moment(start_date)
          .utc()
          .format("MMMM" + conditionalYearFormat)
      : `${moment(start_date).utc().format("MMM")} - ${moment(end_date)
          .utc()
          .format("MMM" + conditionalYearFormat)}`;
  }

  return isSameMonth
    ? `${moment(start_date).utc().format("D")} - ${formatDateToLocale(
        end_date
      )}`
    : `${formatDateToLocale(start_date)} - ${formatDateToLocale(end_date)}`;
};
